'use client';

import React, { useState, useEffect } from 'react';
import RoleCard from './RoleCard';
import FilterBar from './FilterBar';

interface Achievement {
  metric: string;
  value: string;
  description: string;
}

interface Role {
  id: number;
  title: string;
  company: string;
  location: string;
  period: string;
  duration: string;
  industry: string;
  type: string;
  description: string;
  keyResponsibilities: string[];
  achievements: Achievement[];
  skills: string[];
  impact: {
    financial?: string;
    operational?: string;
    compliance?: string;
    team?: string;
  };
}

interface TimelineItem {
  year: string;
  title: string;
  company: string;
  type: 'milestone' | 'role';
}

interface Metric {
  icon: string;
  label: string;
  value: string;
  description: string;
  color: string;
}

const ExperienceInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState('All Industries');
  const [selectedType, setSelectedType] = useState('All Types');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const roles: Role[] = [
    {
      id: 1,
      title: 'Laboratory Director',
      company: 'V-TIC Services Inc',
      location: 'Williston, ND',
      period: '2025',
      duration: '1+ year',
      industry: 'Environmental',
      type: 'Leadership',
      description:
        'Leading comprehensive laboratory operations with full P&L responsibility, managing multi-lab facility and driving strategic growth initiatives.',
      keyResponsibilities: [
        'Direct all laboratory operations including quality control, method validation, and regulatory compliance',
        'Manage P&L responsibility for multi-lab facility with full accountability for cost optimization and revenue generation',
        'Lead cross-functional teams of scientists, technicians, and support staff across multiple departments',
        'Implement and maintain accreditation and compliance programs ensuring zero audit findings',
        'Develop and execute strategic initiatives for laboratory expansion and capability enhancement',
        'Establish client relationships and drive business development',
        'Oversee LIMS implementation and laboratory automation projects improving operational efficiency',
      ],
      achievements: [
        {
          metric: 'Cost Reduction',
          value: '18%',
          description: 'Operational cost savings through process optimization',
        },
        {
          metric: 'Turnaround Time',
          value: '50%',
          description: 'Improvement in sample processing speed',
        },
        {
          metric: 'Accuracy Rate',
          value: '99%',
          description: 'Maintained high accuracy standards',
        },
        {
          metric: 'Compliance Rate',
          value: '100%',
          description: 'State accreditation achieved with zero non-conformances',
        },
        {
          metric: 'Efficiency Gain',
          value: '30%',
          description: 'LIMS system implementation efficiency improvement',
        },
      ],
      skills: [
        'Laboratory Management',
        'Regulatory Compliance',
        'State Accreditation',
        'P&L Management',
        'Team Leadership',
        'LIMS Implementation',
        'Quality Assurance',
        'Method Validation',
        'Business Development',
        'Strategic Planning',
      ],
      impact: {
        financial:
          'Reduced operational costs by 18% through process optimization and resource management',
        operational:
          'Improved sample turnaround time by 50% while maintaining 99% accuracy rate',
        compliance:
          'Successfully led Environmental Lab to attain State Accreditation with zero non-conformances',
        team:
          'Implemented new LIMS system resulting in 30% efficiency gain',
      },
    },
    {
      id: 2,
      title: 'Branch Manager',
      company: 'V-TIC Services Inc',
      location: 'Boston, MA',
      period: '2022 - 2025',
      duration: '3 years',
      industry: 'Environmental',
      type: 'Management',
      description:
        'Managed day-to-day laboratory operations, quality assurance programs, and team development. Reported directly to Stakeholders with Project updates.',
      keyResponsibilities: [
        'Oversee daily operations of laboratory facility',
        'Manage quality assurance programs and compliance initiatives',
        'Report directly to stakeholders with project updates',
        'Develop customized service solutions to drive repeat business',
        'Lead team development and mentorship initiatives',
        'Coordinate ASTM Round Robin Audits',
        'Implement employee retention strategies',
      ],
      achievements: [
        {
          metric: 'Compliance Success',
          value: '100%',
          description: 'Consistent compliance with ASTM Round Robin Audits',
        },
        {
          metric: 'Accreditation',
          value: 'Achieved',
          description: 'State Accreditation with zero non-conformances',
        },
        {
          metric: 'Client Retention',
          value: '90-95%',
          description: 'High client retention through customized solutions',
        },
        {
          metric: 'Employee Retention',
          value: '35%',
          description: 'Improvement through mentorship initiatives',
        },
      ],
      skills: [
        'Laboratory Operations',
        'Quality Assurance',
        'ASTM Compliance',
        'Team Development',
        'Client Relations',
        'Project Management',
        'Mentorship',
        'Stakeholder Communication',
        'Service Development',
        'Employee Retention',
      ],
      impact: {
        operational:
          'Achieved consistent compliance with ASTM Round Robin Audits',
        compliance:
          'Successfully led Environmental Lab to attain State Accreditation with zero non-conformances',
        team:
          'Improved employee retention rate by 35% through mentorship initiatives',
      },
    },
    {
      id: 3,
      title: 'Branch Manager',
      company: 'SGS North America',
      location: 'Houston, TX',
      period: '2020 - 2022',
      duration: '2 years',
      industry: 'Oil & Gas',
      type: 'Management',
      description:
        'Managed day-to-day laboratory operations, quality assurance programs, and team development.',
      keyResponsibilities: [
        'Oversee daily laboratory operations and workflow management',
        'Manage quality assurance programs ensuring compliance',
        'Lead team development and training initiatives',
        'Conduct root cause analysis for quality incidents',
        'Implement process improvements and corrective actions',
        'Coordinate with clients and stakeholders',
        'Maintain laboratory safety and compliance standards',
      ],
      achievements: [
        {
          metric: 'Quality Improvement',
          value: '60%',
          description: 'Reduction in quality incidents through systematic analysis',
        },
        {
          metric: 'Process Optimization',
          value: 'Implemented',
          description: 'Root cause analysis system for continuous improvement',
        },
      ],
      skills: [
        'Laboratory Management',
        'Quality Assurance',
        'Root Cause Analysis',
        'Team Development',
        'Process Improvement',
        'Corrective Actions',
        'Safety Management',
        'Compliance',
        'Client Relations',
        'Training',
      ],
      impact: {
        operational:
          'Reduced quality incidents by 60% through systematic root cause analysis',
        team:
          'Developed high-performing team through effective leadership and training',
      },
    },
    {
      id: 4,
      title: 'Chemical Lab Technician',
      company: 'SGS North America',
      location: 'Houston, TX',
      period: '2017 - 2020',
      duration: '3 years',
      industry: 'Oil & Gas',
      type: 'Technical',
      description:
        'Foundation role performing routine testing and learning laboratory operations from the ground up.',
      keyResponsibilities: [
        'Execute analytical testing using ASTM/GPA methods',
        'Maintain laboratory equipment and perform calibrations',
        'Follow laboratory safety protocols and procedures',
        'Manage sample retention and disposal procedures',
        'Maintain chain-of-custody documentation',
        'Participate in proficiency testing programs',
        'Assist in laboratory equipment maintenance',
      ],
      achievements: [
        {
          metric: 'Training Completion',
          value: 'Achieved',
          description: 'Comprehensive training in ASTM/GPA methods and safety',
        },
        {
          metric: 'Process Improvement',
          value: 'Implemented',
          description: 'Improved sample retention and disposal procedures',
        },
        {
          metric: 'Proficiency Testing',
          value: 'Excellent',
          description: 'Participated with excellent results',
        },
        {
          metric: 'Career Advancement',
          value: 'Promoted',
          description: 'Advanced to Senior Analyst role ahead of schedule',
        },
      ],
      skills: [
        'ASTM Methods',
        'GPA Methods',
        'Laboratory Testing',
        'Equipment Calibration',
        'Laboratory Safety',
        'Chain of Custody',
        'Sample Management',
        'Proficiency Testing',
        'Documentation',
        'Compliance',
      ],
      impact: {
        operational:
          'Improved sample retention and disposal procedures to strengthen chain-of-custody and compliance',
        compliance:
          'Completed comprehensive training in ASTM/GPA methods and laboratory safety',
      },
    },
    {
      id: 5,
      title: 'Field Tech II',
      company: 'MIDCONTINENT COMMUNICATIONS',
      location: 'Williston, ND',
      period: '2015 - Current',
      duration: '10+ years',
      industry: 'Telecommunications',
      type: 'Technical',
      description:
        'Providing technical field services for telecommunications infrastructure, delivering customer support, troubleshooting network issues, and maintaining HFC plant systems while managing projects and inventory.',
      keyResponsibilities: [
        'Maintain and troubleshoot HFC plant infrastructure ensuring network reliability',
        'Perform phone and internet wiring installations for residential and commercial customers',
        'Manage inventory of equipment and supplies for field operations',
        'Provide exceptional customer service while resolving technical issues',
        'Conduct systems analysis to diagnose and resolve network problems',
        'Manage project timelines and coordinate with team members',
        'Utilize Microsoft Office suite for documentation and reporting',
        'Apply networking knowledge to optimize service delivery',
      ],
      achievements: [
        {
          metric: 'Network Uptime',
          value: '99.5%',
          description: 'Maintained high network reliability through proactive maintenance',
        },
        {
          metric: 'Customer Satisfaction',
          value: '95%',
          description: 'Consistently high ratings for technical service quality',
        },
        {
          metric: 'Response Time',
          value: '30%',
          description: 'Improvement in service call response efficiency',
        },
        {
          metric: 'Project Completion',
          value: '100%',
          description: 'On-time completion rate for assigned projects',
        },
      ],
      skills: [
        'Customer Service',
        'Inventory Management',
        'Troubleshooting',
        'Project Management',
        'Systems Analysis',
        'Microsoft Office',
        'Time Management',
        'Networking',
        'HFC Plant Maintenance',
        'Technical Installation',
      ],
      impact: {
        operational:
          'Maintained 99.5% network uptime through proactive maintenance and rapid issue resolution',
        team:
          'Consistently delivered exceptional customer service with 95% satisfaction ratings',
      },
    },
    {
      id: 6,
      title: 'General Manager',
      company: 'JSL SPEEDPAK SERVICES INC',
      location: 'Miami, FL',
      period: '2012 - 2015',
      duration: '3 years',
      industry: 'Logistics',
      type: 'Leadership',
      description:
        'Led multi-site operations for logistics and freight forwarding company, overseeing strategic planning, budgeting, business development, and operational excellence across multiple locations.',
      keyResponsibilities: [
        'Manage multi-site operations with full P&L responsibility',
        'Develop and execute strategic plans for business growth and operational efficiency',
        'Implement budgeting and cost control measures to optimize profitability',
        'Conduct cost/benefit analysis for major operational decisions',
        'Create policies and procedures to standardize operations',
        'Provide customer service solutions and manage client relationships',
        'Lead business process improvement initiatives',
        'Negotiate contracts with vendors and service providers',
        'Plan and coordinate logistics operations for international shipments',
        'Develop new business opportunities and expand service offerings',
      ],
      achievements: [
        {
          metric: 'Revenue Growth',
          value: '25%',
          description: 'Increased revenue through new business development',
        },
        {
          metric: 'Cost Reduction',
          value: '18%',
          description: 'Operational cost savings through process improvements',
        },
        {
          metric: 'Client Retention',
          value: '92%',
          description: 'High customer retention through service excellence',
        },
        {
          metric: 'Multi-Site Management',
          value: '5',
          description: 'Locations managed simultaneously',
        },
      ],
      skills: [
        'Multi-Site Operations',
        'Strategic Planning',
        'Budgeting',
        'Cost Analysis',
        'Policy Development',
        'Customer Service Management',
        'Process Improvement',
        'Contract Negotiation',
        'Logistics Planning',
        'Business Development',
      ],
      impact: {
        financial:
          'Achieved 25% revenue growth and 18% cost reduction through strategic planning and operational excellence',
        operational:
          'Successfully managed multi-site operations improving efficiency and service delivery',
        team:
          'Built high-performing teams across multiple locations driving business success',
      },
    },
    {
      id: 7,
      title: 'Branch Manager',
      company: 'JSL SPEEDPAK SERVICES INC',
      location: 'Miami, FL',
      period: '2008 - 2012',
      duration: '4 years',
      industry: 'Logistics',
      type: 'Management',
      description:
        'Managed branch operations including regulatory compliance, facilities management, team development, and back office operations for freight forwarding services.',
      keyResponsibilities: [
        'Ensure regulatory compliance with TSA and FAA security policies',
        'Manage facilities operations and maintenance',
        'Handle crisis communication and resolution effectively',
        'Oversee back office operations including documentation and reporting',
        'Train, coach, and mentor staff to build high-performing teams',
        'Implement and enforce security policies mandated by TSA and FAA',
        'Serve as IT representative for system integrations and technology implementations',
        'Build cohesive teams through effective leadership and communication',
        'Manage inventory and coordinate logistics operations',
      ],
      achievements: [
        {
          metric: 'Compliance Rate',
          value: '100%',
          description: 'Perfect record in TSA and FAA security audits',
        },
        {
          metric: 'Team Development',
          value: '30+',
          description: 'Staff members trained and mentored',
        },
        {
          metric: 'System Integration',
          value: '3',
          description: 'Major IT systems successfully implemented',
        },
        {
          metric: 'Operational Efficiency',
          value: '22%',
          description: 'Improvement in branch operational performance',
        },
      ],
      skills: [
        'Regulatory Compliance',
        'Facilities Management',
        'Crisis Management',
        'Back Office Operations',
        'Staff Training',
        'Security Policy Implementation',
        'IT Coordination',
        'Team Building',
        'Inventory Management',
        'Leadership',
      ],
      impact: {
        compliance:
          'Maintained perfect compliance record with TSA and FAA security regulations',
        operational:
          'Improved branch efficiency by 22% through effective management and process optimization',
        team:
          'Developed 30+ staff members through comprehensive training and mentorship programs',
      },
    },
    {
      id: 8,
      title: 'Warehouse Manager',
      company: 'JSL SPEEDPAK SERVICES INC',
      location: 'Miami, FL',
      period: '2006 - 2008',
      duration: '2 years',
      industry: 'Logistics',
      type: 'Management',
      description:
        'Oversaw warehouse operations, inventory management, compliance with aviation security regulations, and team development while coordinating with cargo airlines for international shipments.',
      keyResponsibilities: [
        'Manage inventory control and warehouse organization',
        'Coordinate vendor maintenance for heavy equipment and machinery',
        'Ensure compliance with TSA and FAA security requirements',
        'Hire and train warehouse staff for optimal performance',
        'Forecast manpower requirements based on seasonal workload and targets',
        'Promote warehouse safety culture and reduce workplace accidents',
        'Coordinate with cargo airlines to expedite international shipments',
        'Oversee receiving, processing, and shipping operations',
      ],
      achievements: [
        {
          metric: 'Safety Improvement',
          value: '15%',
          description: 'Reduction in lost time accidents through safety initiatives',
        },
        {
          metric: 'Compliance Rate',
          value: '100%',
          description: 'Perfect TSA and FAA security compliance record',
        },
        {
          metric: 'Team Management',
          value: '25+',
          description: 'Warehouse staff hired and trained',
        },
        {
          metric: 'Shipment Efficiency',
          value: '20%',
          description: 'Improvement in international shipment processing',
        },
      ],
      skills: [
        'Inventory Management',
        'Vendor Coordination',
        'TSA/FAA Compliance',
        'Staff Hiring and Training',
        'Workforce Planning',
        'Safety Management',
        'Airline Coordination',
        'Warehouse Operations',
        'Equipment Maintenance',
        'Logistics Coordination',
      ],
      impact: {
        operational:
          'Improved shipment efficiency by 20% through effective coordination with cargo airlines',
        compliance:
          'Maintained perfect compliance with TSA and FAA security regulations',
        team:
          'Reduced lost time accidents by 15% through safety culture and training initiatives',
      },
    },
    {
      id: 9,
      title: 'Warehouse Attendant',
      company: 'JSL SPEEDPAK SERVICES INC',
      location: 'Miami, FL',
      period: '2000 - 2006',
      duration: '6 years',
      industry: 'Logistics',
      type: 'Technical',
      description:
        'Foundation role in warehouse operations, handling shipment processing, equipment operation, maintaining safety standards, and achieving security certifications.',
      keyResponsibilities: [
        'Receive, process, and package shipments for transport',
        'Ensure strict adherence to time constraints and deadlines',
        'Operate forklifts and heavy machinery safely and efficiently',
        'Load and unload cargo trucks and containers in timely manner',
        'Deliver freight to cargo airlines using company vehicles',
        'Maintain organized and safe warehouse environment',
        'Comply with FAA and TSA security regulations',
      ],
      achievements: [
        {
          metric: 'STA Certification',
          value: 'Achieved',
          description: 'Security Training Accreditation in compliance with regulations',
        },
        {
          metric: 'Safety Record',
          value: '100%',
          description: 'Perfect safety record operating heavy equipment',
        },
        {
          metric: 'On-Time Delivery',
          value: '98%',
          description: 'Consistently met strict shipping deadlines',
        },
        {
          metric: 'Shipments Processed',
          value: '10,000+',
          description: 'Total shipments handled during tenure',
        },
      ],
      skills: [
        'Shipment Processing',
        'Forklift Operation',
        'Heavy Machinery',
        'Time Management',
        'Safety Compliance',
        'FAA/TSA Regulations',
        'Warehouse Organization',
        'Cargo Handling',
        'Delivery Operations',
        'Attention to Detail',
      ],
      impact: {
        operational:
          'Maintained 98% on-time delivery rate while processing 10,000+ shipments',
        compliance:
          'Achieved STA certification demonstrating commitment to security regulations',
      },
    },
  ];

  const filteredRoles = roles.filter((role) => {
    const industryMatch =
      selectedIndustry === 'All Industries' || role.industry === selectedIndustry;
    const typeMatch = selectedType === 'All Types' || role.type === selectedType;
    return industryMatch && typeMatch;
  });

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary via-primary/95 to-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-8">
            <div className="h-32 bg-muted rounded-xl" />
            <div className="h-64 bg-muted rounded-xl" />
            <div className="h-96 bg-muted rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <FilterBar
        selectedIndustry={selectedIndustry}
        onIndustryChange={setSelectedIndustry}
        selectedType={selectedType}
        onTypeChange={setSelectedType}
      />

      <div className="space-y-6">
        {filteredRoles.length > 0 ? (
          filteredRoles.map((role) => <RoleCard key={role.id} role={role} />)
        ) : (
          <div className="bg-card border border-border rounded-xl p-12 text-center">
            <p className="text-text-secondary text-lg">
              No roles match the selected filters. Please adjust your criteria.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExperienceInteractive;